import type { SelectionPartition } from "@/utils/selections/partitionSelections";

export interface SelectionCounts {
  adoptable: number;
  blocked: number;
  usable: number;
  total: number;
}

export interface ModalCopy {
  title: string;
  subline: string;
  /** null renders a single dismiss button — there is nothing to add. */
  primaryLabel: string | null;
  dismissLabel: string;
  showFooterCount: boolean;
}

export function countSelections(p: SelectionPartition): SelectionCounts {
  const adoptable = p.adoptable.brandSets.length + p.adoptable.segments.length;
  const blocked =
    p.blocked.brandSets.length +
    p.blocked.segments.length +
    p.blocked.brands.length +
    p.blocked.filters.length;
  // Filters are counted here deliberately: the previous usableCount omitted
  // them, under-reporting what the link actually carried.
  const usable =
    p.usable.brandSets.length +
    p.usable.segments.length +
    p.usable.brands.length +
    p.usable.filters.length;
  return { adoptable, blocked, usable, total: adoptable + blocked + usable };
}

const items = (n: number): string => (n === 1 ? "item" : "items");

// Precondition: callers must not invoke this for a partition with neither
// adoptable nor blocked items. The orchestrator only opens the modal when
// hasAdoptable(p) || hasBlocked(p) (see ValidateSelections.tsx), so the
// no-adoptable branch below can assume blocked > 0.
export function modalCopy(p: SelectionPartition): ModalCopy {
  const { adoptable, blocked, usable } = countSelections(p);

  if (adoptable === 0) {
    return {
      title: "Some of this link isn't available to you",
      subline:
        usable > 0
          ? "The rest is already yours and has been selected."
          : "None of the items in this link are available to you.",
      primaryLabel: null,
      dismissLabel: "Close",
      showFooterCount: false,
    };
  }

  return {
    title: `${adoptable} ${items(adoptable)} can be added to your account`,
    subline:
      blocked > 0
        ? `${blocked} more ${items(blocked)} in this link ${blocked === 1 ? "isn't" : "aren't"} available to you.`
        : "Everything else in this link is already yours and has been selected.",
    primaryLabel: "Add",
    dismissLabel: "Not now",
    showFooterCount: true,
  };
}
